import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { Language, Translations } from '../i18n/types';
import { en } from '../i18n/en';
import { it } from '../i18n/it';

const STORAGE_KEY = 'aisthetix-language';

const translationMap: Record<Language, Translations> = { en, it };

interface LanguageContextValue {
  language: Language;
  t: Translations;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: 'en',
  t: en,
  setLanguage: () => {},
  toggleLanguage: () => {},
});

function getInitialLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'it') return stored;
  } catch {
    // localStorage unavailable (e.g., in iframe with restrictive policies)
  }
  return 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // Silently fail if localStorage is unavailable
    }
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage(language === 'en' ? 'it' : 'en');
  }, [language, setLanguage]);

  return (
    <LanguageContext.Provider
      value={{
        language,
        t: translationMap[language],
        setLanguage,
        toggleLanguage,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
